// Hooks das TABULAÇÕES (desfechos de chamada): o catálogo da org, o mapeamento
// para o HubSpot e o resumo do período. Mesmo padrão de use-sip.ts:
//   - `src/api/dispositions.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// As chaves abaixo são as que já estavam espalhadas por cinco telas, string por
// string — centralizar não é renomear. O React Query invalida por PREFIXO, e uma
// chave que divergisse uma letra viraria dado velho na tela, sem erro nenhum.
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createDisposition,
  deleteDisposition,
  getDispositions,
  getDispositionsSummary,
  getHubspotDispositionOptions,
  updateDisposition,
  type DispositionCategory,
  type DispositionsSummaryQuery,
} from "@/api/dispositions";

// O catálogo do portal do HubSpot muda raramente e cada gestor abre a tela de
// tabulações várias vezes por sessão — daí o teto alto de frescor.
const HUBSPOT_OPTIONS_STALE_MS = 5 * 60 * 1000;

// As chaves DESTE domínio. Ressalvas herdadas que valem leitura:
//
//  - `active` (["dispositions"]) é PREFIXO de `catalog` (["dispositions",
//    "all"]), e isso é o mecanismo, não um acidente: as três mutações do
//    catálogo invalidam ["dispositions"] e derrubam as DUAS entradas de uma vez.
//    São dois recortes do mesmo endpoint — `active` traz só as ativas (é o que o
//    detalhe da chamada e a tabela de ligações usam para achar um rótulo) e
//    `catalog` traz também as desativadas (o gestor precisa reativar, e o editor
//    de automação precisa mostrar o filtro que já está salvo num desfecho morto).
//  - `hubspotOptions` (["hubspot-disposition-options"]) fica FORA daquele
//    prefixo de propósito: criar/ativar/desativar um desfecho não tem nada a ver
//    com o catálogo do PORTAL, e dentro do prefixo toda mutação não relacionada
//    bateria no HubSpot de novo. Comentário original da tela, preservado aqui.
//  - `summary` (["dispositions-summary", query]) também fica fora, e não é
//    ["dispositions", "summary", …]: é outro endpoint (/v1/calls/…), de outro
//    ritmo, e editar o catálogo não muda o que já foi tabulado no período.
//    Divergência pré-existente da tela de relatório; preservada.
export const dispositionKeys = {
  active: ["dispositions"] as const,
  catalog: ["dispositions", "all"] as const,
  hubspotOptions: ["hubspot-disposition-options"] as const,
  summary: (query: DispositionsSummaryQuery) => ["dispositions-summary", query] as const,
};

// --- Catálogo ---

// Só as tabulações ATIVAS. Duas telas sem parentesco leem esta MESMA entrada de
// cache (o detalhe da chamada e a tabela de ligações), cada uma com o seu gate:
// o diálogo só busca quando abre, a tabela só quando a linha tem tabulação.
export function useDispositions(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: dispositionKeys.active,
    queryFn: () => getDispositions(),
    enabled: options.enabled,
  });
}

// Catálogo COMPLETO (inclui as desativadas). Quem gerencia precisa reativar, e o
// editor de automação precisa enxergar um filtro salvo num desfecho já morto —
// sem trazê-lo, o id segue viajando a cada save e ninguém consegue removê-lo.
export function useDispositionsCatalog(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: dispositionKeys.catalog,
    queryFn: () => getDispositions({ includeInactive: true }),
    enabled: options.enabled,
  });
}

// Resultados de chamada do portal do HubSpot. `enabled` é o gate de gestão (o
// back também restringe por papel). Lista vazia = portal sem resposta ou sem
// resultados: a tela mostra o aviso, não um erro.
export function useHubspotDispositionOptions(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: dispositionKeys.hubspotOptions,
    queryFn: getHubspotDispositionOptions,
    enabled: options.enabled,
    staleTime: HUBSPOT_OPTIONS_STALE_MS,
  });
}

// A invalidação é a MESMA para as três mutações do catálogo, então é do hook.
// `useCallback([queryClient])` e não uma seta solta: a identidade estável é o que
// permite passá-la adiante (ou pô-la num array de dependência) sem recriar a
// cadeia a cada render.
function useInvalidateDispositions() {
  const queryClient = useQueryClient();
  return useCallback(
    () => void queryClient.invalidateQueries({ queryKey: dispositionKeys.active }),
    [queryClient],
  );
}

// Criar um desfecho. Os `mutationFn` daqui chamam a função de api dentro de uma
// seta: o TanStack invoca o mutationFn com (variáveis, contexto), e uma função
// de api que aceite um segundo parâmetro receberia o contexto como se fosse dado.
export function useCreateDisposition(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateDispositions();
  return useMutation({
    mutationFn: (input: { label: string; category: DispositionCategory; sortOrder?: number }) =>
      createDisposition(input),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

// Ativar/desativar. "Excluir" é SOFT-DELETE (DELETE devolve active=false), então
// o par ida-e-volta é DELETE / PATCH { active: true } — o histórico de chamadas
// já tabuladas continua sabendo dizer o rótulo. O ramo mora aqui porque é
// exatamente o que a tela fazia inline, e as duas pontas invalidam igual.
export function useToggleDisposition(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateDispositions();
  return useMutation({
    mutationFn: async (disposition: { id: string; active: boolean }) => {
      if (disposition.active) await deleteDisposition(disposition.id);
      else await updateDisposition(disposition.id, { active: true });
    },
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

// Mapear a tabulação para um resultado de chamada do portal do HubSpot. É um
// PATCH do mesmo registro, mas hook à parte: o toast e o estado de "salvando" do
// select são desta ação, não do botão de ativar/desativar ao lado.
export function useMapHubspotDisposition(
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateDispositions();
  return useMutation({
    mutationFn: (variables: { id: string; hubspotDispositionId: string | null }) =>
      updateDisposition(variables.id, { hubspotDispositionId: variables.hubspotDispositionId }),
    onSuccess: () => {
      handlers.onSuccess?.();
      invalidate();
    },
    onError: handlers.onError,
  });
}

// --- Resumo (relatório) ---

// `placeholderData` mantém os números anteriores na tela enquanto o novo período
// carrega — sem isso o relatório pisca em branco a cada mudança de data.
export function useDispositionsSummary(query: DispositionsSummaryQuery) {
  return useQuery({
    queryKey: dispositionKeys.summary(query),
    queryFn: () => getDispositionsSummary(query),
    placeholderData: (prev) => prev,
  });
}
